/**
 * Lecture des états qu'une gateway publie (`to_srv/signal_state` et `to_srv/pg`), stockés tels
 * quels dans `config.stateSignal` et `config.pg` et poussés en direct sur
 * `/topic/gateway/{id}/state`.
 *
 * Valeurs lues dans le firmware gateway_stm32 (signal.c, task_sensors.c, auto_sync.c) :
 * - `signal_state` = `{"json":1, "signal":"In air", "jesd_w":"data"}` ;
 * - `pg` = `{…, "GPSDO":"LOCKED", "Auto-Sync":"Locked", "Auto-Sync-Locked":1}`.
 * Avant le 2026-11-02, le front attendait `true`/`1` et affichait Signal et GPSDO toujours rouges.
 */

/** ok = vert, wait = en cours (jaune), ko = à traiter (rouge), off = coupé ou absent (gris). */
export type GatewayStateTone = 'ok' | 'wait' | 'ko' | 'off';

export interface GatewayStateReading {
  tone: GatewayStateTone;
  /** Clé i18n sous `siteViewMap.state.*`, ou null pour afficher {@link raw} tel quel. */
  key: string | null;
  /** Valeur reçue de la gateway, pour l'infobulle et une valeur inconnue. */
  raw: string;
}

function parseJson(raw: string | null | undefined): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' && !Array.isArray(obj) ? (obj as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

const SIGNAL: Record<string, [GatewayStateTone, string]> = {
  'In air': ['ok', 'signal.inAir'],
  Init: ['wait', 'signal.init'],
  Requested: ['wait', 'signal.requested'],
  Computing: ['wait', 'signal.computing'],
  Error: ['ko', 'signal.error'],
  'uBlaze Timeout': ['ko', 'signal.timeout'],
};

/**
 * État du signal radio (`signal_state.signal`). « No-signal » est attendu quand la RF a été coupée
 * ({@code isEmitting === false}) : gris ; sinon c'est une panne : rouge.
 */
export function readSignalState(
  stateSignal: string | null | undefined,
  isEmitting?: boolean
): GatewayStateReading | null {
  const value = parseJson(stateSignal)?.['signal'];
  if (value == null || value === '') return null;
  const raw = String(value);
  if (raw === 'No-signal') {
    return isEmitting === false ? { tone: 'off', key: 'signal.rfOff', raw } : { tone: 'ko', key: 'signal.none', raw };
  }
  const known = SIGNAL[raw];
  return known ? { tone: known[0], key: known[1], raw } : { tone: 'ko', key: null, raw };
}

const AUTO_SYNC_PHASE: Record<string, string> = {
  'Waiting GNSS & UBLAZE': 'autoSync.waitingGnss',
  'OCXO Warm-up': 'autoSync.warmup',
  'Locking Freq': 'autoSync.lockingFreq',
  'Freq locked': 'autoSync.freqLocked',
  'Locking T0': 'autoSync.lockingT0',
  Enabled: 'autoSync.enabled',
};

/**
 * Auto-synchro entre gateways. `Auto-Sync-Locked` (auto_sync.c `sync_lock_graph`) vaut 1 une fois
 * verrouillée, -1 en régulation T0 à moins de 3 m, -2 sinon ; la phase est dans `Auto-Sync`.
 * Tout ce qui n'est pas verrouillé est « en cours » (jaune) : la gateway converge seule.
 */
export function readAutoSync(pg: string | null | undefined): GatewayStateReading | null {
  const obj = parseJson(pg);
  if (!obj || !('Auto-Sync-Locked' in obj)) return null;
  const lock = Number(obj['Auto-Sync-Locked']);
  const phase = obj['Auto-Sync'] == null ? '' : String(obj['Auto-Sync']);
  const raw = phase ? `${obj['Auto-Sync-Locked']} · ${phase}` : String(obj['Auto-Sync-Locked']);
  if (lock === 1) return { tone: 'ok', key: 'autoSync.locked', raw };
  if (lock === -1) return { tone: 'wait', key: 'autoSync.nearLock', raw };
  const key = AUTO_SYNC_PHASE[phase];
  if (key) return { tone: 'wait', key, raw };
  return phase ? { tone: 'wait', key: null, raw } : { tone: 'wait', key: 'autoSync.notLocked', raw };
}

const GPSDO: Record<string, [GatewayStateTone, string]> = {
  LOCKED: ['ok', 'gpsdo.locked'],
  UNLOCKED: ['wait', 'gpsdo.unlocked'],
  DISCONNECTED: ['off', 'gpsdo.disconnected'],
};

/** Verrouillage de l'oscillateur GPSDO (`pg.GPSDO`). Absent sur certaines cartes : rien à lire. */
export function readGpsdo(pg: string | null | undefined): GatewayStateReading | null {
  const value = parseJson(pg)?.['GPSDO'];
  if (value == null || value === '') return null;
  const raw = String(value);
  const known = GPSDO[raw];
  return known ? { tone: known[0], key: known[1], raw } : { tone: 'ko', key: null, raw };
}
